import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentContext } from '../../ContentContext';
import { ContentApi } from '../../../../shared/api/contentApi';
import GroupParameterCreateDialog from '../components/NewGroupParameterDialog.jsx';
import { resolvePagedTotals } from '../../../../shared/helpers/pagination';
import formatDate from '../../../../shared/helpers/formatDate';
import UuidCell from '../../../../components/ui/UuidCell';
import TableCellPreview from '../../../../components/ui/TableCellPreview';
import { useListTableState } from '../../../../shared/hooks/useListTableState';

import { BaseTable } from '../../../../components/ui/ant/Table';
import {Flex, Select, Input} from 'antd';

export default function GroupParameterPage() {
    const navigate = useNavigate();
    const [createOpen, setCreateOpen] = useState(false);

    // серверная пагинация
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const {
        qInput,
        setQInput,
        qDebounced,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        groupBy,
        setGroupBy,
        page,
        setPage,
        pageSize,
        setPageSize,
        total,
        setTotal,
        totalPages,
        setTotalPages,
        safePage,
    } = useListTableState({
        initialSortBy: 'name',
        initialSortDir: 'asc',
        initialGroupBy: 'none',
        initialPageSize: 6,
        debounceMs: 1450,
    });
    // const [items, setItems] = useState([]);

    const [groupParameters, setGroupParameters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!projectId || !versionId) {
            setGroupParameters([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError('');

        ContentApi.getParameterGroups(
            {
                search: qDebounced || undefined,
                page,
                pageSize,
            },
            { signal: ac.signal }
        )
            .then((data) => {
                setGroupParameters(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e.message || 'Ошибка загрузки');
            })
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [page, pageSize, projectId, qDebounced, setTotal, setTotalPages, versionId]);

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        Группы параметров
                    </h1>
                    <p className="text-sm text-zinc-200">
                        Данные с SbContentAPI
                    </p>
                </div>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => setCreateOpen(true)}
                >
                    Создать группу параметров
                </button>
                <Flex flex orientation="horizontal" wrap="wrap" align="end" gap="medium">
                    <Flex flex vertical>
                        <label className="eyebrow mb-1" htmlFor="group-parameter-search">
                            Поиск
                        </label>
                        <Input
                            size="medium"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            placeholder="Имя или systemName…"
                            id="group-parameter-search"
                            className=" w-64"
                            name="group-parameter-search"
                        />
                    </Flex>
                </Flex>
            </header>

            <GroupParameterCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(created) => {
                    const newId = created?.id || created?.groupParameterId;
                    if (newId) {
                        navigate(`/group-parameters/${newId}`);
                    } else {
                        setPage(1);
                    }
                }}
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <BaseTable
                        columns={[
                            {
                                key: 'id',
                                header: 'ID',
                                render: (value) => <UuidCell value={value} />,
                            },
                            { key: "name", header: "Имя", sortable: true },
                            { key: "systemName", header: "Системное имя", sortable: true },
                            { key: "isLocked", header: "Заблокирован", groupable: true },
                            {
                                key: 'parentGroupId',
                                header: 'ID родительской группы',
                                groupable: true,
                                render: (value) => <UuidCell value={value} />,
                            },
                            {
                                key: 'updatedAt',
                                header: 'Обновлено',
                                sortable: true,
                                render: (value) => <span title={value || '-'}>{formatDate(value)}</span>,
                            },
                        ]}
                        data={groupParameters}
                        searchQuery={qInput}
                        searchKeys={['name', 'systemName', 'description']}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={(key, dir) => {
                            setSortBy(key);
                            if (dir) setSortDir(dir);
                        }}
                        groupBy={groupBy === 'none' ? undefined : groupBy}
                        onGroupByChange={(key) => setGroupBy(key ?? 'none')}
                        pagination={{
                            current: safePage,
                            pageSize,
                            total,
                        }}
                        onChange={(pagination) => {
                            if (pagination?.current) {
                                setPage(pagination.current);
                            }
                            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
                                setPageSize(pagination.pageSize);
                            }
                        }}
                        groupByResolver={(row, field) => {
                            if (field === 'isLocked') {
                                return row?.isLocked ? 'Заблокированные' : 'Редактируемые';
                            }
                            return row?.[field] || 'Без родителя';
                        }}
                        onRowClick={(row) => navigate(`/content/group-parameters/${row.id}`)}
                    />
                </>
            )}
        </section>
    );
}